import React from "react";
import PropTypes from "prop-types";

import formatCurrency from "modules/appointments/helper/formatCurrency";

/**
 * "Why is only part of this coming from the wallet?" — the rules behind the
 * eligible amount, ported from bridge's WalletRulesInfoBox.
 *
 * The eligible amount is the MINIMUM of several independent limits, so a bare
 * number ("₹200 from wallet") is unexplainable at the counter: the operator
 * cannot tell a patient whether the shortfall is an empty wallet, a service
 * sub-limit, a per-transaction cap or a co-pay rule. Each row is rendered only
 * when the server says that limit applies, so the box shows the rules that
 * actually bound THIS booking rather than a generic policy list.
 *
 * Every field name here is the sublimit response's own
 * (GET /v1/appointment/getpaymentsublimit); the shape is shared with
 * validPaymentOptions in helper/paymentLinkConstants.js.
 */

/** A limit is either a percentage (unit 1) or an amount (unit 2). */
const formatByUnit = (value, unit) => {
  if (value == null || unit == null) return "-";
  const n = typeof value === "number" ? value : parseFloat(value);
  if (isNaN(n)) return "-";
  if (unit === 1) return `${n % 1 !== 0 ? n.toFixed(2) : n.toFixed(0)}%`;
  if (unit === 2) return formatCurrency(n);
  return "-";
};

const WalletRulesInfoBox = ({ sublimit }) => {
  const unlimited = (sublimit && sublimit.isUnlimitedWallet) || false;
  const category = sublimit && sublimit.category;
  const service = sublimit && sublimit.service;

  const showService = !!(service && service.applicable === true);
  const showCategory = !!(
    category &&
    category.applicable === true &&
    category.sublimit &&
    category.sublimit.enabled === true
  );
  const showPerTxn = !!(
    category &&
    category.applicable === true &&
    category.perTransactionLimit &&
    category.perTransactionLimit.enabled === true
  );
  const showCopay = !!(
    category &&
    category.applicable === true &&
    category.copayLimit &&
    category.copayLimit.enabled === true
  );

  /* The "minimum of the above" line only means something with more than one
     rule in play. */
  const showFooter =
    [showService, showCategory, showPerTxn, showCopay].filter(Boolean).length > 1;

  const bal = (b) =>
    `${formatCurrency(b && b.available)} out of ${formatCurrency(b && b.total)}`;

  return (
    <div className="pl-rules">
      {/* An unlimited wallet has no balance to report — only an eligible amount. */}
      {!unlimited && (
        <p className="pl-rules-head">
          Available wallet balance:{" "}
          <strong>{formatCurrency(sublimit && sublimit.zylaWallet && sublimit.zylaWallet.available)}</strong>
        </p>
      )}
      <p className="pl-rules-head">
        Eligible wallet amount:{" "}
        <strong>{formatCurrency(sublimit && sublimit.useZylaWallet)}</strong>
      </p>

      {showService && (
        <p className="pl-rules-row">
          Available sub-limit {(service && service.serviceCardName) || ""}:{" "}
          {bal(service && service.sublimitBalance)}
        </p>
      )}
      {showCategory && (
        <p className="pl-rules-row">
          {/* Bridge names this one "prescribed diagnostics" only when a service
              limit is shown beside it, so the two rows are distinguishable. */}
          Available sub-limit{showService ? " prescribed diagnostics" : ""}:{" "}
          {bal(category && category.sublimitBalance)}
        </p>
      )}
      {showPerTxn && (
        <p className="pl-rules-row">
          Per transaction limit:{" "}
          {formatByUnit(category.perTransactionLimit.value, category.perTransactionLimit.unit)}
        </p>
      )}
      {showCopay && (
        <p className="pl-rules-row">
          Self co-pay limit:{" "}
          {formatByUnit(category.copayLimit.value, category.copayLimit.unit)}
        </p>
      )}

      {showFooter && (
        <p className="pl-rules-note">
          The eligible wallet amount is the minimum of the rules above.
        </p>
      )}
    </div>
  );
};

WalletRulesInfoBox.propTypes = {
  /** The sublimit response, verbatim. */
  sublimit: PropTypes.object,
};

export default WalletRulesInfoBox;
